import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import useOutsideAlerter from '../../hooks/useOutsideAlerter';
import './style.scss';

interface JUNOSelectOption {
  name: string;
  value: string | number;
  notification?: boolean;
}
interface JUNOSelectProps {
  options: Array<JUNOSelectOption>;
  onChange?: (string) => void;
  placeHolder?: string;
  dropUp?: boolean;
  theme?: 'light' | 'dark' | 'brand';
  style?: React.CSSProperties;
}

const JUNOSelect: React.FC<JUNOSelectProps> = ({
  options,
  onChange,
  placeHolder,
  dropUp = false,
  theme = 'light',
  style = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // if we pass a placeholder, no option is selected initially
  const [selectedValue, setSelectedValue] = useState(placeHolder ? '' : options[0].value);

  // close if we click anywhere else
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, e => {
    setIsOpen(false);
  });

  // set the value, fire callback
  const onClickOption = value => {
    setSelectedValue(value);
    onChange && onChange(value);
  };

  // filter & create selected item JSX
  const selectedOption = options
    .filter(item => item.value == selectedValue)
    .map((item, i) => {
      return <div key={i}>{item.name}</div>;
    });

  // if there are any notifications in here show on default
  const anyNotifications = options.find(option => option.notification);

  // animation variants
  const JUNOSelectVariants = {
    open: {
      opacity: 1,
      y: 0,
      display: 'flex',
      borderRadius: dropUp ? '4px 4px 0 0' : '0 0 4px 4px',
    },
    closed: {
      opacity: 0,
      y: dropUp ? -20 : 20,
      display: 'none',
    },
  };
  // animation variants
  const borderRadiusVariants = {
    open: {
      borderRadius: dropUp ? '0 0 4px 4px' : '4px 4px 0 0',
    },
    closed: {
      borderRadius: '4px 4px 4px 4px',
    },
  };

  return (
    <div className={`JUNOSelect theme_${theme}`} onClick={e => setIsOpen(isOpen => !isOpen)} ref={wrapperRef}>
      <motion.div
        className={`JUNOSelect_selected`}
        whileTap={{
          backgroundColor: `var(--junoselect-${theme}-whileclick-bg)`,
        }}
        variants={borderRadiusVariants}
        animate={isOpen ? 'open' : 'closed'}
        transition={{ duration: 0 }}
        style={style}
      >
        {placeHolder && selectedValue === '' ? <div>{placeHolder}</div> : <div>{selectedOption}</div>}
        <i className={`fa-solid fa-chevron-down`}></i>
        {anyNotifications && <div className={`JUNOSelect_notification`} />}
      </motion.div>
      <motion.div
        className={`JUNOSelect_options ${dropUp && ' drop_up'}`}
        initial={`closed`}
        variants={JUNOSelectVariants}
        animate={isOpen ? 'open' : 'closed'}
      >
        {options.map((item, i) => {
          return (
            <div className={`JUNOSelect_option`} key={i} onClick={e => onClickOption(item.value)}>
              {item.name}
              {item.notification && <div className={`JUNOSelect_option_notification`} />}
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default JUNOSelect;
